import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';


import { NZ_MODAL_DATA, NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzTreeSelectComponent } from 'ng-zorro-antd/tree-select';
import { ClassificationCodeService, TreatmentRecordsService } from '@beautyapp/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DrugListComponent } from '@beautyapp/shared';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { SelectComponent } from '../../../../../form/src/lib/select/select.component';
import { DatepickerComponent } from '../../../../../form/src/lib/datepicker/datepicker.component';

@UntilDestroy()
@Component({
  selector: 'beautyapp-create-treatment',
  imports: [
    ReactiveFormsModule,
    SelectComponent,
    NzModalModule,
    NzButtonModule,
    TranslateModule,
    DatepickerComponent,
    NzTreeSelectComponent,
    NzOptionComponent,
    NzSelectComponent
],
  templateUrl: './create-treatment.component.html',
  styleUrl: './create-treatment.component.css'
})
export class CreateTreatmentComponent implements OnInit {
  private fb = inject(FormBuilder);

  data = inject(NZ_MODAL_DATA);
  modalService = inject(NzModalService);
  modal = inject(NzModalRef);
  classificationCodeService = inject(ClassificationCodeService);
  treatmentRecordsService = inject(TreatmentRecordsService);
  translateService = inject(TranslateService);

  medicalSpecialties = this.classificationCodeService.getMedicalSpecialty();
  bloodComponents = this.classificationCodeService.getBloodComponents();

  form!: FormGroup;
  treatmentTypes = [
    {
      type: 'CLINICAL_DIAGNOSTIC',
      label: 'კლინიკურ-დიაგნოსტიკური გამოკვლევა'
    },
    {
      type: 'OTHER_ACTIVITY',
      label: 'თანმხლები  აქტივობა'
    },
    {
      type: 'LAB_TEST',
      label: 'ლაბორატორიული გამოკვლევები'
    },
    {
      type: 'CONSULTATION',
      label: 'კონსულტაციები'
    },
    {
      type: 'PRESCRIPTION',
      label: 'დანიშნულება'
    },
    {
      type: 'TRANSFUSION',
      label: 'სისხლის გადასხმა'
    },
    {
      type: 'ANESTHESIA',
      label: 'ანესთეზიის ეპიზოდი'
    },
    {
      type: 'SURGERY',
      label: 'ქირურგიული ჩარევა'
    },
    {
      type: 'OPERATION_PROTOCOL',
      label: 'ოპერაციის ოქმი'
    }
  ]; // Match entity enums
  // 'CLINICAL_DIAGNOSTIC',
  // 'OTHER_ACTIVITY',
  // 'LAB_TEST',
  // 'CONSULTATION',
  // 'PRESCRIPTION',
  // 'TRANSFUSION',
  // 'ANESTHESIA',
  // 'SURGERY',
  // 'OPERATION_PROTOCOL',
  selectedType?: string;

  ngOnInit() {
    // The laboratory tree is loaded on request now: the service is root-provided
    // and used to fetch it the moment anything injected it, which put a
    // 30-second RMQ call on screens that never show these codes.
    this.classificationCodeService.loadLaboratoryCodes();
    this.form = this.fb.group({
      type: [''],
      icd10Id: [null],
      ncspId: [null],
      procedureStartDate: [''],
      procedureEndDate: [''],
      procedureResult: [''],

      // Additional Form Fields
      labCodeId: [''],
      labExamText: [''],
      specialtyCode: [null],
      consultantName: [''],
      consultationDate: [''],
      consultationComment: [''],
      drugId: [null],
      productId: [null],
      drugName: [''],
      drugCount: [null],
      wasUsed: [null],
      bloodComponent: [null],
      indicationOfTransfusion: [null],
      quantity: [null],
      transfusionComment: [''],
      anesthesiaComment: [''],
      protocolNo: [''],
      protocolText: ['']
    });

    this.form.get('type')?.valueChanges.subscribe((type) => {
      this.selectedType = type;
    });

    if (this.data) {
      if (this.data.type) {
        this.selectedType = this.data.type;
      }

      if (this.data.data) {
        console.log(this.data.data);
        this.form.patchValue({
          treatmentType: this.data.type,
          ...this.data.data,
          productId: this.data.data?.drug?.productId || null,
        });

        // Find and set NCSP node if ncspId exists
        if (this.data.data.ncspId) {
          const ncspNode = this.findNcspNodeById(this.data.data.ncspId);
          if (ncspNode) {
            console.log('Found NCSP node:', ncspNode);
            // You can use the found node here, e.g., to display it in the UI
            // or store it in a component property for later use
          }
        }

        // Find and set ICD10 node if icd10Id exists
        if (this.data.data.icd10Id) {
          const icd10Node = this.findIcd10NodeById(this.data.data.icd10Id);
          if (icd10Node) {
            console.log('Found ICD10 node:', icd10Node);
            // You can use the found node here, e.g., to display it in the UI
            // or store it in a component property for later use
          }
        }
      }
    }

  }


  submit(): void {
    if (this.form.invalid) {
      return;
    }
    console.log(this.data.data);

    // Check if in template mode
    if (this.data.isTemplateMode) {
      const ncsp = this.findNcspNodeById(this.form.value?.ncspId, this.classificationCodeService.classificationNCSPCodes());
      const icd10 = this.findIcd10NodeById(this.form.value?.icd10Id, this.classificationCodeService.classificationICDCodes());

      console.log('NCSP:', ncsp);
      console.log('ICD10:', icd10);

      // In template mode, just return the form data without API call
      const treatmentData = {
        ...this.form.value,
        ncsp,
        icd10,
        wasUsed: this.form.value?.wasUsed ? +this.form.value.wasUsed : undefined
      };
      this.modal.close(treatmentData);
      return;
    }

    // Normal mode - make API calls
    if (this.data.data?.id) {
      this.treatmentRecordsService.update(this.data.data.id, {
        ...this.form.value,
        id: this.data.data.id,
        ehrId: this.data.ehrId,
        wasUsed: +this.form.value?.wasUsed
      }).subscribe(res => {
        this.modal.close(res);
      });
    } else {
      this.treatmentRecordsService.create({
        ...this.form.value,
        ehrId: this.data.ehrId,
        wasUsed: +this.form.value?.wasUsed
      }).subscribe(res => {
        this.modal.close(res);
      });
    }
  }

  chooseDrug() {
    this.modalService.create({
      nzOkDisabled: true,
      nzCancelText: this.translateService.instant('general.close'),
      nzTitle: this.translateService.instant('ERX.chooseProductTitle'),
      nzContent: DrugListComponent,
      nzWidth: '60%',
    }).afterClose.pipe(
      untilDestroyed(this)
    ).subscribe(res => {
      console.log(res);
      if (res) {
        this.form.patchValue({
          drugId: res.id,
          productId: res.productId,
          drugName: res.geoTradeName
        });
      }
    });
  }

  onNcspChange(node: any) {
    if (node) {
      const ncspData = {
        code: node.key,
        name: node.title
      };
      this.form.patchValue({
        ncspId: ncspData.code
      });
    }
  }

  /**
   * Recursively search for NCSP node by ID in tree structure
   * @param ncspId - The ID to search for
   * @param nodes - The tree nodes to search in (defaults to all NCSP codes)
   * @returns The found node or null
   */
  findNcspNodeById(ncspId: string | null, nodes?: any[]): any | null {
    if (!ncspId) return null;

    const searchNodes = nodes || this.classificationCodeService.classificationNCSPCodes();

    for (const node of searchNodes) {
      // Check if current node matches
      if (node.key === ncspId || node.value === ncspId) {
        return node;
      }

      // Recursively search children
      if (node.children && node.children.length > 0) {
        const found = this.findNcspNodeById(ncspId, node.children);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  /**
   * Recursively search for ICD10 node by ID in tree structure
   * @param icd10Id - The ID to search for
   * @param nodes - The tree nodes to search in (defaults to all ICD codes)
   * @returns The found node or null
   */
  findIcd10NodeById(icd10Id: string | null, nodes?: any[]): any | null {
    if (!icd10Id) return null;

    const searchNodes = nodes || this.classificationCodeService.classificationICDCodes();

    for (const node of searchNodes) {
      // Check if current node matches
      if (node.key === icd10Id || node.value === icd10Id) {
        return node;
      }

      // Recursively search children
      if (node.children && node.children.length > 0) {
        const found = this.findIcd10NodeById(icd10Id, node.children);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }
}
